import { useEffect, useState } from 'react';
import { Cookie, Shield, Clock, Info, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

interface CookieInfo {
  name: string;
  purpose: string;
  duration: string;
  provider: string;
}

interface CategoryInfo {
  title: string;
  description: string;
  cookies: CookieInfo[];
}

interface CookiePolicy {
  version: string;
  last_updated: string;
  categories: {
    necessary: CategoryInfo;
    functional: CategoryInfo;
    analytics: CategoryInfo;
    marketing: CategoryInfo;
  };
}

export default function CookiesPolicyPage() {
  const [policy, setPolicy] = useState<CookiePolicy | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/cookies/policy')
      .then(res => res.json())
      .then(data => {
        setPolicy(data);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load cookie policy:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50">
      {/* Header */}
      <div className="bg-zinc-900 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <Link to="/" className="text-sm text-emerald-400 hover:text-emerald-300 mb-4 inline-block">
            ← Retour à l'accueil
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-8 h-8 text-emerald-500" />
            <h1 className="text-4xl font-bold">Politique de cookies</h1>
          </div>
          <p className="text-lg text-zinc-300">
            Dernière mise à jour : {policy?.last_updated || '31 janvier 2026'}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto"></div>
            <p className="text-zinc-400 mt-4">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Introduction */}
            <section className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                <Info className="w-6 h-6 text-emerald-500" />
                Qu'est-ce qu'un cookie ?
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                Un cookie est un petit fichier texte stocké sur votre appareil (ordinateur, tablette, smartphone) 
                lorsque vous visitez un site web. Les cookies permettent au site de mémoriser vos actions et préférences 
                (comme la langue, le thème, etc.) pendant une certaine période, afin que vous n'ayez pas à les ressaisir 
                à chaque visite ou navigation.
              </p>
              
              <h2 className="text-2xl font-bold mt-8 mb-4 flex items-center gap-2">
                <Shield className="w-6 h-6 text-emerald-500" />
                Comment utilisons-nous les cookies ?
              </h2>
              <p className="text-zinc-300 leading-relaxed">
                Nous utilisons des cookies pour améliorer votre expérience sur notre plateforme. Certains cookies sont 
                strictement nécessaires au fonctionnement du site, tandis que d'autres sont optionnels et nécessitent 
                votre consentement. Vous pouvez à tout moment modifier vos préférences de cookies.
              </p>
            </section>

            {/* Catégories de cookies */}
            {policy && (
              <section className="space-y-8">
                <h2 className="text-3xl font-bold mb-6">Catégories de cookies</h2>

                {/* Cookies nécessaires */}
                {policy.categories.necessary && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-emerald-500/10 rounded-lg">
                        <Shield className="w-6 h-6 text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {policy.categories.necessary.title}
                        </h3>
                        <p className="text-zinc-400 text-sm">
                          {policy.categories.necessary.description}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">
                        Toujours actifs
                      </span>
                    </div>
                    
                    {policy.categories.necessary.cookies.length > 0 && (
                      <div className="mt-4 space-y-3">
                        {policy.categories.necessary.cookies.map((cookie, idx) => (
                          <div key={idx} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                            <div className="flex items-start justify-between mb-2">
                              <code className="text-sm font-mono text-emerald-400">{cookie.name}</code>
                              <div className="flex items-center gap-2 text-xs text-zinc-500">
                                <Clock className="w-3 h-3" />
                                {cookie.duration}
                              </div>
                            </div>
                            <p className="text-sm text-zinc-400">{cookie.purpose}</p>
                            <p className="text-xs text-zinc-500 mt-2">Fournisseur : {cookie.provider}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Cookies fonctionnels */}
                {policy.categories.functional && policy.categories.functional.cookies.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-blue-500/10 rounded-lg">
                        <Cookie className="w-6 h-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {policy.categories.functional.title}
                        </h3>
                        <p className="text-zinc-400 text-sm">
                          {policy.categories.functional.description}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-blue-500/20 text-blue-400 text-xs font-medium rounded-full">
                        Optionnel
                      </span>
                    </div>
                    
                    <div className="mt-4 space-y-3">
                      {policy.categories.functional.cookies.map((cookie, idx) => (
                        <div key={idx} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                          <div className="flex items-start justify-between mb-2">
                            <code className="text-sm font-mono text-blue-400">{cookie.name}</code>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <Clock className="w-3 h-3" />
                              {cookie.duration}
                            </div>
                          </div>
                          <p className="text-sm text-zinc-400">{cookie.purpose}</p>
                          <p className="text-xs text-zinc-500 mt-2">Fournisseur : {cookie.provider}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cookies analytiques */}
                {policy.categories.analytics && policy.categories.analytics.cookies.length > 0 && (
                  <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="p-2 bg-purple-500/10 rounded-lg">
                        <Info className="w-6 h-6 text-purple-500" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-2">
                          {policy.categories.analytics.title}
                        </h3>
                        <p className="text-zinc-400 text-sm">
                          {policy.categories.analytics.description}
                        </p>
                      </div>
                      <span className="px-3 py-1 bg-purple-500/20 text-purple-400 text-xs font-medium rounded-full">
                        Optionnel
                      </span>
                    </div>
                    
                    <div className="mt-4 space-y-3">
                      {policy.categories.analytics.cookies.map((cookie, idx) => (
                        <div key={idx} className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                          <div className="flex items-start justify-between mb-2">
                            <code className="text-sm font-mono text-purple-400">{cookie.name}</code>
                            <div className="flex items-center gap-2 text-xs text-zinc-500">
                              <Clock className="w-3 h-3" />
                              {cookie.duration}
                            </div>
                          </div>
                          <p className="text-sm text-zinc-400">{cookie.purpose}</p>
                          <p className="text-xs text-zinc-500 mt-2">Fournisseur : {cookie.provider}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Vos droits */}
            <section className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6">
              <h2 className="text-2xl font-bold mb-4 text-emerald-400">Vos droits</h2>
              <div className="space-y-3 text-zinc-300">
                <p>Conformément au RGPD et à la directive ePrivacy, vous disposez des droits suivants :</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Droit de refuser les cookies non essentiels</li>
                  <li>Droit de retirer votre consentement à tout moment</li>
                  <li>Droit d'accéder aux informations sur les cookies utilisés</li>
                  <li>Droit de supprimer les cookies de votre navigateur</li>
                </ul>
                <p className="mt-4">
                  Pour modifier vos préférences de cookies, vous pouvez cliquer sur le bouton ci-dessous ou 
                  supprimer les cookies directement depuis les paramètres de votre navigateur.
                </p>
                <button
                  onClick={() => {
                    localStorage.removeItem('cookie_consent');
                    window.location.reload();
                  }}
                  className="mt-4 px-6 py-3 bg-emerald-500 text-white rounded-lg font-medium hover:bg-emerald-400 transition-colors"
                >
                  Modifier mes préférences
                </button>
              </div>
            </section>

            {/* Contact */}
            <section className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-bold mb-4">Contact</h2>
              <p className="text-zinc-300">
                Pour toute question concernant notre utilisation des cookies, vous pouvez nous contacter à :
              </p>
              <p className="text-zinc-300">
                <strong>Email :</strong>{' '}
                <a href="mailto:contact@totemium.com" className="text-emerald-400 hover:text-emerald-300">
                  contact@totemium.com
                </a>
              </p>
            </section>

            {/* Ressources */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h2 className="text-2xl font-bold mb-4">Ressources utiles</h2>
              <div className="space-y-3">
                <a
                  href="https://www.cnil.fr/fr/cookies-et-autres-traceurs/regles/cookies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  CNIL - Cookies et traceurs
                </a>
                <a
                  href="https://gdpr.eu/cookies/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  RGPD - Guide des cookies
                </a>
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
